import { useState, useCallback } from 'react'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable'
import { useDroppable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import type { DragEndEvent } from '@dnd-kit/core'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import { staffApi } from '../../../api/staff'
import type { AuthSession } from '../../../types/plans'
import {
  getStageName,
  getServices,
  getServiceQty,
  getServiceSortOrder,
  isExecuted,
} from './constructorTypes'
import type { ConstructorStage, DoctorOption } from './constructorTypes'
import { DragHandleIcon } from './MisDragSource'
import { SortableServiceRow } from './SortableServiceRow'
import { CashOfferForm } from './shared/CashOfferForm'
import type { CashOfferFormFields } from './shared/CashOfferForm'

export function SortableStageCard({
  stage,
  planId,
  bootstrap,
  session,
  capabilities,
  doctors,
  onReload,
  dropTargetId,
}: {
  stage: ConstructorStage
  planId: number
  bootstrap: TenantBootstrapModel
  session: AuthSession
  capabilities?: { has_coefficients?: boolean }
  doctors: DoctorOption[]
  onReload: () => void
  dropTargetId: string
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: stage.id,
    data: { kind: 'stage' },
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const [expanded, setExpanded] = useState(true)
  const [editingTitle, setEditingTitle] = useState(false)
  const [stageTitleVal, setStageTitleVal] = useState(getStageName(stage))
  const [adding, setAdding] = useState(false)
  const [newSvcName, setNewSvcName] = useState('')
  const [newSvcTooth, setNewSvcTooth] = useState('')
  const [newSvcQty, setNewSvcQty] = useState(1)
  const [newSvcPrice, setNewSvcPrice] = useState(0)
  const [saving, setSaving] = useState(false)
  const { setNodeRef: setDropNodeRef, isOver } = useDroppable({ id: dropTargetId })
  const setStageNodeRef = useCallback((node: HTMLElement | null) => {
    setNodeRef(node)
    setDropNodeRef(node)
  }, [setNodeRef, setDropNodeRef])

  const initialOffer = stage.cash_offer ?? null
  const [offerExpanded, setOfferExpanded] = useState<boolean>(initialOffer !== null)
  const [offerFields, setOfferFields] = useState<CashOfferFormFields>({
    amount: initialOffer?.amount != null ? String(initialOffer.amount) : '',
    deadline: initialOffer?.deadline ?? '',
    method: initialOffer?.method ?? '',
    note: initialOffer?.note ?? '',
  })
  const [offerSaving, setOfferSaving] = useState<boolean>(false)

  const [descVal, setDescVal] = useState<string>(stage.description ?? '')
  const [descSaving, setDescSaving] = useState<boolean>(false)

  async function handleSaveDescription() {
    const trimmed = descVal.trim()
    if (trimmed === (stage.description ?? '')) return
    setDescSaving(true)
    try {
      await staffApi.updateStage(bootstrap, session, planId, stage.id, { description: trimmed || null })
      onReload()
    } catch {
      // ignore
    } finally {
      setDescSaving(false)
    }
  }

  const services = getServices(stage).slice().sort((a, b) => getServiceSortOrder(a) - getServiceSortOrder(b))
  const svcSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))
  const [svcOrder, setSvcOrder] = useState<number[]>(services.map(s => s.id))

  async function handleSaveTitle() {
    if (stageTitleVal.trim() === '' || stageTitleVal === getStageName(stage)) {
      setEditingTitle(false)
      return
    }
    setSaving(true)
    try {
      await staffApi.updateStage(bootstrap, session, planId, stage.id, { title: stageTitleVal })
      onReload()
    } catch {
      // ignore
    } finally {
      setSaving(false)
      setEditingTitle(false)
    }
  }

  async function handleDeleteStage() {
    if (!confirm(`Удалить этап "${getStageName(stage)}"?`)) return
    try {
      await staffApi.deleteStage(bootstrap, session, planId, stage.id)
      onReload()
    } catch {
      // ignore
    }
  }

  async function handleAddService() {
    if (newSvcName.trim() === '') return
    setSaving(true)
    try {
      await staffApi.addService(bootstrap, session, planId, stage.id, {
        name: newSvcName,
        tooth: newSvcTooth,
        qty: newSvcQty,
        price: newSvcPrice,
        sort_order: services.length,
      })
      setNewSvcName('')
      setNewSvcTooth('')
      setNewSvcQty(1)
      setNewSvcPrice(0)
      setAdding(false)
      onReload()
    } catch {
      // ignore
    } finally {
      setSaving(false)
    }
  }

  async function handleDeleteService(svcId: number) {
    try {
      await staffApi.deleteService(bootstrap, session, planId, stage.id, svcId)
      onReload()
    } catch {
      // ignore
    }
  }

  async function handleToggleExecuted(svc: Parameters<typeof isExecuted>[0]) {
    try {
      await staffApi.updateService(bootstrap, session, planId, stage.id, svc.id, {
        is_executed: !isExecuted(svc) ? 1 : 0,
      })
      onReload()
    } catch {
      // ignore
    }
  }

  async function handleSvcDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIdx = svcOrder.indexOf(active.id as number)
    const newIdx = svcOrder.indexOf(over.id as number)
    const newOrder = arrayMove(svcOrder, oldIdx, newIdx)
    setSvcOrder(newOrder)

    try {
      await staffApi.reorderServices(bootstrap, session, planId, stage.id, newOrder)
    } catch {
      // ignore
    }
  }

  async function handleDoctorChange(value: string) {
    const newId = value === '' ? null : Number(value)
    const newName = newId === null ? null : (doctors.find((d) => d.id === newId)?.display_name ?? null)
    try {
      await staffApi.updateStage(bootstrap, session, planId, stage.id, {
        doctor_id: newId,
        doctor_name: newName,
      })
      onReload()
    } catch {
      // ignore
    }
  }

  async function handleSaveCashOffer() {
    setOfferSaving(true)
    try {
      const amountNum = offerFields.amount.trim() === ''
        ? null
        : Number(offerFields.amount.replace(/\s+/g, '').replace(',', '.'))
      const payload =
        amountNum == null && offerFields.note.trim() === '' && offerFields.deadline.trim() === ''
          ? null
          : {
              amount: amountNum != null && Number.isFinite(amountNum) ? amountNum : null,
              note: offerFields.note.trim(),
              deadline: offerFields.deadline.trim() || null,
              method: offerFields.method.trim() || null,
            }
      await staffApi.updateStage(bootstrap, session, planId, stage.id, { cash_offer: payload })
      onReload()
    } catch {
      // ignore
    } finally {
      setOfferSaving(false)
    }
  }

  async function handleClearCashOffer() {
    setOfferFields({ amount: '', deadline: '', method: '', note: '' })
    setOfferSaving(true)
    try {
      await staffApi.updateStage(bootstrap, session, planId, stage.id, { cash_offer: null })
      onReload()
    } catch {
      // ignore
    } finally {
      setOfferSaving(false)
    }
  }

  const stageTotal = services.reduce((sum, svc) => {
    const unit = svc.unit_price ?? svc.price ?? 0
    const qty = getServiceQty(svc)
    const total = svc.total_price ?? unit * qty
    return sum + total
  }, 0)

  const hasSavedOffer = !!(stage.cash_offer && (stage.cash_offer.amount != null || stage.cash_offer.note || stage.cash_offer.deadline))

  return (
    <div
      ref={setStageNodeRef}
      style={style}
      className={`overflow-hidden rounded-md border bg-white transition-colors ${
        isOver ? 'border-blue-400 ring-2 ring-blue-100' : 'border-slate-300'
      }`}
    >
      {/* Stage header */}
      <div className="flex items-center gap-2 border-b border-slate-300 bg-slate-50 px-2 py-1.5">
        <button
          type="button"
          className="cursor-grab text-slate-400 hover:text-slate-600"
          {...attributes}
          {...listeners}
          title="Перетащить этап"
        >
          <DragHandleIcon className="h-4 w-4" />
        </button>

        {editingTitle ? (
          <input
            className="flex-1 rounded border border-blue-300 px-2 py-1 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-400"
            value={stageTitleVal}
            onChange={(event) => setStageTitleVal(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') void handleSaveTitle()
              if (event.key === 'Escape') setEditingTitle(false)
            }}
            onBlur={() => void handleSaveTitle()}
            autoFocus
            disabled={saving}
          />
        ) : (
          <button
            type="button"
            className="flex-1 text-left text-sm font-semibold text-slate-800 hover:text-blue-600"
            onClick={() => {
              setEditingTitle(true)
              setStageTitleVal(getStageName(stage))
            }}
          >
            {getStageName(stage)}
          </button>
        )}

        <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-medium text-slate-500 ring-1 ring-slate-200">
          {services.length} поз.
        </span>
        <span className="text-xs font-semibold text-slate-700">
          {stageTotal.toLocaleString('ru-RU')} ₽
        </span>
        <button
          type="button"
          className="text-slate-400 hover:text-slate-600"
          onClick={() => setExpanded((v) => !v)}
          title={expanded ? 'Свернуть этап' : 'Развернуть этап'}
        >
          <svg className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <button
          type="button"
          className="text-red-400 hover:text-red-600"
          onClick={() => void handleDeleteStage()}
          title="Удалить этап"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>

      {/* Doctor selector */}
      {expanded && doctors.length > 0 && (
        <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50/60 px-3 py-1.5 text-xs">
          <span className="text-slate-500">Врач этапа:</span>
          <select
            className="flex-1 rounded border border-slate-300 bg-white px-2 py-0.5 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
            value={stage.doctor_id ?? ''}
            onChange={(e) => void handleDoctorChange(e.target.value)}
          >
            <option value="">— Не выбран —</option>
            {doctors.map((doc) => (
              <option key={doc.id} value={doc.id}>
                {doc.display_name}{doc.role_label ? ` · ${doc.role_label}` : ''}
              </option>
            ))}
          </select>
          {stage.doctor_name ? (
            <span className="hidden text-[10px] text-slate-500 sm:inline" title={`ID ${stage.doctor_id ?? ''}`}>
              {stage.doctor_name}
            </span>
          ) : null}
        </div>
      )}

      {/* Stage comment */}
      {expanded && (
        <div className="border-b border-slate-100 bg-white px-3 py-2">
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wide text-slate-500">Комментарий к этапу</span>
            <textarea
              rows={2}
              className="w-full resize-none rounded border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-400"
              value={descVal}
              placeholder="Необязательный комментарий, виден пациенту..."
              disabled={descSaving}
              onChange={(e) => setDescVal(e.target.value)}
              onBlur={() => void handleSaveDescription()}
            />
          </label>
        </div>
      )}

      {/* Cash offer section */}
      {expanded && (
        <div className="border-b border-amber-200 bg-amber-50/60 px-3 py-1.5 text-xs">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded border border-amber-300 bg-white px-2 py-0.5 text-amber-700 hover:bg-amber-100"
              onClick={() => setOfferExpanded((v) => !v)}
              title="Условия оплаты наличными"
            >
              <span aria-hidden>₽</span>
              <span>Спец. цена за наличные</span>
              <svg className={`h-3 w-3 transition-transform ${offerExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {!offerExpanded && stage.cash_offer && (stage.cash_offer.amount != null || stage.cash_offer.note) ? (
              <span className="text-[11px] text-amber-800">
                {stage.cash_offer.amount != null
                  ? `${Number(stage.cash_offer.amount).toLocaleString('ru-RU')} ₽`
                  : null}
                {stage.cash_offer.deadline ? ` · до ${stage.cash_offer.deadline}` : ''}
                {stage.cash_offer.note ? ` · ${stage.cash_offer.note}` : ''}
              </span>
            ) : null}
          </div>
          {offerExpanded ? (
            <>
              <CashOfferForm
                fields={offerFields}
                saving={offerSaving}
                hasSavedOffer={hasSavedOffer}
                variant="stage"
                onFieldChange={(patch) => setOfferFields((prev) => ({ ...prev, ...patch }))}
                onSave={() => void handleSaveCashOffer()}
                onClear={() => void handleClearCashOffer()}
              />
              <span className="mt-1 block text-[10px] text-amber-800/80">
                Будет акцентировано в публичном шеринге плана пациенту.
              </span>
            </>
          ) : null}
        </div>
      )}

      {/* Services table */}
      {expanded && (
        <div className={`relative ${isOver ? 'bg-blue-50/60' : ''}`}>
          <DndContext sensors={svcSensors} collisionDetection={closestCenter} onDragEnd={handleSvcDragEnd}>
            <SortableContext items={svcOrder} strategy={verticalListSortingStrategy}>
              <table className="w-full table-fixed border-collapse text-sm">
                <thead>
                  <tr className="border-b border-slate-300 bg-slate-100 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                    <th className="w-8 border-r border-slate-200 px-1 py-1 text-center"></th>
                    <th className="border-r border-slate-200 px-2 py-1 text-left">Услуга</th>
                    <th className="w-14 border-r border-slate-200 px-2 py-1 text-center">Зуб</th>
                    <th className="w-12 border-r border-slate-200 px-2 py-1 text-center">Кол-во</th>
                    <th className="w-24 border-r border-slate-200 px-2 py-1 text-right">Цена</th>
                    <th className="w-28 border-r border-slate-200 px-2 py-1 text-right">Сумма</th>
                    <th className="w-10 border-r border-slate-200 px-1 py-1 text-center">✓</th>
                    <th className="w-10 px-1 py-1 text-center"></th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((svc) => (
                    <SortableServiceRow
                      key={svc.id}
                      svc={svc}
                      hasCoefficients={capabilities?.has_coefficients}
                      onToggleExecuted={() => void handleToggleExecuted(svc)}
                      onDelete={() => void handleDeleteService(svc.id)}
                    />
                  ))}
                  {services.length === 0 ? (
                    <tr>
                      <td colSpan={8} className={`px-3 py-3 text-center text-xs ${isOver ? 'text-blue-600' : 'text-slate-400'}`}>
                        {isOver ? 'Отпустите услугу здесь' : 'В этапе пока нет услуг — перетащите из списка МИС или добавьте вручную'}
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </SortableContext>
          </DndContext>

          {/* Add service form */}
          <div className="border-t border-slate-200 bg-slate-50 p-2">
            {adding ? (
              <div className="rounded-md border border-blue-200 bg-blue-50 p-2">
                <div className="mb-2 grid grid-cols-2 gap-2">
                  <input
                    className="col-span-2 rounded border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                    placeholder="Название услуги"
                    value={newSvcName}
                    onChange={(event) => setNewSvcName(event.target.value)}
                  />
                  <input
                    className="rounded border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                    placeholder="Зуб/челюсть"
                    value={newSvcTooth}
                    onChange={(event) => setNewSvcTooth(event.target.value)}
                  />
                  <input
                    type="number"
                    className="rounded border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                    placeholder="Кол-во"
                    value={newSvcQty}
                    min={1}
                    onChange={(event) => setNewSvcQty(Number(event.target.value))}
                  />
                  <input
                    type="number"
                    className="col-span-2 rounded border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                    placeholder="Цена (₽)"
                    value={newSvcPrice}
                    min={0}
                    onChange={(event) => setNewSvcPrice(Number(event.target.value))}
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="flex-1 rounded bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                    onClick={() => void handleAddService()}
                    disabled={saving || newSvcName.trim() === ''}
                  >
                    {saving ? 'Сохранение...' : 'Добавить'}
                  </button>
                  <button
                    type="button"
                    className="rounded bg-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-300"
                    onClick={() => setAdding(false)}
                  >
                    Отмена
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="w-full rounded border border-dashed border-slate-300 bg-white py-1.5 text-xs text-slate-500 hover:border-blue-400 hover:text-blue-600"
                onClick={() => setAdding(true)}
              >
                + Добавить услугу
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
